import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import {
  isJoinInFlight,
  JoinLessonErrorKind,
  useJoinLesson,
  useTodayLessonLink,
  useToaster,
} from 'core';
import { ToastTypes } from 'components/atoms';
import { UserRole } from 'context';
import { formatLessonStartShort, isLessonToday } from 'services/LessonTimeService';
import { LessonType } from 'types';

import { LessonZoomJoin } from './LessonZoomJoin';
import { buildJoinWindow } from './joinWindow';

/** Текст тоста на каждую причину отказа входа. */
const JOIN_FAILURE_TEXT: Partial<Record<JoinLessonErrorKind, string>> = {
  [JoinLessonErrorKind.notConfigured]: 'lesson.join.linkNotSetYet',
  [JoinLessonErrorKind.network]: 'lesson.join.networkError',
  [JoinLessonErrorKind.sessionExpired]: 'lesson.join.sessionExpired',
};

/** Как часто пересчитывать окно входа. */
const TICK_MS = 30_000;

/**
 * Текущее время как СОСТОЯНИЕ, а не разовый `DateTime.now()` при рендере.
 *
 * Окно входа зависит от «сейчас», но у занятия время статичное — посчитанное
 * один раз при монтировании оно так и застыло бы: студент, открывший страницу
 * за два часа до занятия и просто ждущий, никогда не увидел бы, как кнопка
 * включится (и как она погаснет по окончании). Тик раз в 30 секунд достаточно
 * точен для границы, заданной в минутах.
 */
const useNow = (): DateTime => {
  const [now, setNow] = useState(() => DateTime.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(DateTime.now()), TICK_MS);
    return () => clearInterval(timer);
  }, []);
  return now;
};

export interface ILessonZoomJoinContainer {
  currentLesson?: LessonType | null;
  activeRole: UserRole;
  /**
   * Группа «без расписания»: окно входа продолжает работать как обычно, но не
   * называет ни дату начала, ни «занятие сегодня» — иначе расписание утекло бы
   * именно там, где мы его прячем.
   */
  hideSchedule?: boolean;
  className?: string;
}

/**
 * Spec 0013: wires the join button to `lmsJoinLesson`. The new tab is opened
 * SYNCHRONOUSLY in the click handler (popup-blocker safe); the join URL only
 * arrives from the mutation because the JWT lives in Apollo headers — a plain
 * <a href> can't be authorized.
 */
export const LessonZoomJoinContainer = memo(
  ({ currentLesson, activeRole, hideSchedule = false, className }: ILessonZoomJoinContainer) => {
    const { t } = useTranslation();
    const { groupId, lessonId } = useParams();
    const { addToast } = useToaster();

    const groupLessonId = Number(lessonId);
    // Занятость — из реестра модуля, а не из состояния экземпляра: на время
    // обновления токена страница перемонтируется, а вход ещё идёт.
    const [joinLesson, { loading: joinLoading }] = useJoinLesson(groupLessonId);
    const { setTodayLink, clearTodayLink, setLoading, clearLoading } = useTodayLessonLink(
      Number(groupId),
    );

    const isTeacher = activeRole === UserRole.TEACHER;
    const hasJoinLink = Boolean(currentLesson?.hasJoinLink);

    const now = useNow();
    const {
      open: joinWindowOpen,
      ended,
      startsAt,
    } = useMemo(
      () => buildJoinWindow(now, currentLesson?.startsAt, currentLesson?.endsAt),
      [now, currentLesson?.startsAt, currentLesson?.endsAt],
    );

    // Only the STUDENT is gated: a teacher legitimately opens the room early to
    // set up, and owns the temporary-link panel right below.
    const joinDisabled = !isTeacher && !joinWindowOpen;

    const joinHint = useMemo(() => {
      if (!joinDisabled) {
        return '';
      }
      if (ended) {
        return `${t('lesson.join.lessonEnded')}`;
      }
      // Без расписания подсказка обязана объяснить серую кнопку, но не может
      // назвать дату — иначе расписание утечёт через неё.
      if (hideSchedule) {
        return `${t('lesson.join.notAvailableYet')}`;
      }
      // ТЗ 0100: местное время студента, при чужом поясе — с пометкой пояса.
      return startsAt
        ? `${t('lesson.join.startsAt', { when: formatLessonStartShort(startsAt) })}`
        : '';
    }, [joinDisabled, ended, startsAt, hideSchedule, t]);

    const showJoinError = useCallback(() => {
      addToast('join_lesson', {
        type: ToastTypes.danger,
        text: `${t('lesson.join.joinError')}`,
      });
    }, [addToast, t]);

    const showPopupBlocked = useCallback(() => {
      addToast('join_lesson', {
        type: ToastTypes.warning,
        text: `${t('lesson.join.popupBlocked')}`,
      });
    }, [addToast, t]);

    /**
     * Причина отказа → понятный студенту текст. Приостановку не тостим: её уже
     * обработал errorLink (уводит на /invoices), второй тост был бы шумом.
     */
    const showJoinFailure = useCallback(
      (kind: JoinLessonErrorKind | null) => {
        if (kind === JoinLessonErrorKind.suspended) {
          return;
        }
        const textKey =
          JOIN_FAILURE_TEXT[kind || JoinLessonErrorKind.unknown] || 'lesson.join.joinError';
        addToast('join_lesson', {
          type: ToastTypes.danger,
          text: `${t(textKey)}`,
        });
      },
      [addToast, t],
    );

    /**
     * Вкладку, открытую заранее, студент мог закрыть, пока шёл запрос (или её
     * не дал открыть блокировщик). Пробуем новую — жеста уже нет, браузер может
     * её заблокировать; тогда честно говорим об этом.
     */
    const openInFreshTab = useCallback(
      (redirectUrl: string) => {
        const fresh = window.open('', '_blank');
        if (!fresh) {
          showPopupBlocked();
          return;
        }
        fresh.opener = null;
        fresh.location.href = redirectUrl;
      },
      [showPopupBlocked],
    );

    const onJoin = useCallback(() => {
      // Реестр, а не только `joinLoading`: клик мог прийти раньше, чем
      // перемонтированный экземпляр перерисовался с «занято».
      if (joinLoading || isJoinInFlight(groupLessonId)) {
        return;
      }
      // open the tab synchronously within the user gesture, then redirect it
      const joinWindow = window.open('', '_blank');
      if (joinWindow) {
        // Restores the `noopener` guarantee we give up by needing a handle on
        // the tab (same as the diplomas download): about:blank is still
        // same-origin here, and the disowning survives the navigation.
        joinWindow.opener = null;
      }
      const closeJoinWindow = () => {
        if (joinWindow && !joinWindow.closed) {
          joinWindow.close();
        }
      };

      // useJoinLesson never rejects and retries an expired token once — the
      // pre-opened tab stays open for the whole attempt, retry included.
      joinLesson(groupLessonId)
        .then(({ success, redirectUrl, error }) => {
          if (success && redirectUrl) {
            if (joinWindow && !joinWindow.closed) {
              joinWindow.location.href = redirectUrl;
            } else {
              openInFreshTab(redirectUrl);
            }
            return;
          }
          closeJoinWindow();
          showJoinFailure(error);
        })
        .catch(() => {
          closeJoinWindow();
          showJoinFailure(JoinLessonErrorKind.unknown);
        });
    }, [joinLesson, joinLoading, groupLessonId, openInFreshTab, showJoinFailure]);

    const onSetLink = useCallback(
      async (joinUrl: string) => {
        const success = await setTodayLink(joinUrl);
        if (!success) {
          showJoinError();
        }
        return success;
      },
      [setTodayLink, showJoinError],
    );

    const onClearLink = useCallback(async () => {
      const success = await clearTodayLink();
      if (!success) {
        showJoinError();
      }
    }, [clearTodayLink, showJoinError]);

    if (!currentLesson || (!hasJoinLink && !isTeacher)) {
      return null;
    }

    return (
      <LessonZoomJoin
        className={className}
        showJoinButton={hasJoinLink}
        isToday={!hideSchedule && isLessonToday(currentLesson, now)}
        joinDisabled={joinDisabled}
        joinHint={joinHint}
        joinLoading={joinLoading}
        onJoin={onJoin}
        isTeacher={isTeacher}
        linkOverrideActive={Boolean(currentLesson.linkOverrideActive)}
        setLoading={setLoading}
        clearLoading={clearLoading}
        onSetLink={onSetLink}
        onClearLink={onClearLink}
      />
    );
  },
);
